export type WCAGLevel = "A" | "AA" | "AAA";

export type WCAGCriterion = "2.1.1" | "2.1.2" | "2.1.4" | "2.4.3" | "2.4.7" | "4.1.2";

export interface WCAGViolation {
  criterion: WCAGCriterion;
  level: WCAGLevel;
  message: string;
  impact: "critical" | "serious" | "moderate" | "minor";
  suggestion: string;
  element?: HTMLElement;
  context?: Record<string, unknown>;
}

export interface WCAGWarning {
  criterion: WCAGCriterion;
  level: WCAGLevel;
  message: string;
  suggestion: string;
  element?: HTMLElement;
  context?: Record<string, unknown>;
}

export interface WCAGCheck {
  criterion: WCAGCriterion;
  level: WCAGLevel;
  passed: boolean;
  description: string;
}

export interface WCAGComplianceResult {
  compliant: boolean;
  level: WCAGLevel;
  violations: WCAGViolation[];
  warnings: WCAGWarning[];
  passed: WCAGCheck[];
}

export interface PrintableCharacterInfo {
  isPrintable: boolean;
  category: "letter" | "number" | "symbol" | "whitespace" | "control" | "modifier";
  requiresWCAGCompliance: boolean;
}

export interface CharacterKeyShortcutCompliance {
  isPrintableCharacterOnly: boolean;
  complianceMechanisms: {
    canDisable: boolean;
    canRemap: boolean;
    scopedToFocus: boolean;
  };
  compliant: boolean;
  recommendation?: string;
}

export interface FocusManagementCompliance {
  hasFocusIndicator: boolean;
  focusOrderLogical: boolean;
  noKeyboardTrap: boolean;
  compliant: boolean;
  violations: WCAGViolation[];
  warnings: WCAGWarning[];
}

const wcagLevelCache = new Set<string>(["A", "AA", "AAA"]);
const wcagCriterionCache = new Set<string>(["2.1.1", "2.1.2", "2.1.4", "2.4.3", "2.4.7", "4.1.2"]);
const printableCharacterCache = new Map<string, PrintableCharacterInfo>();
let defaultWCAGComplianceResult: WCAGComplianceResult | null = null;

export function isValidWCAGLevel(value: string): value is WCAGLevel {
  return wcagLevelCache.has(value);
}

export function isValidWCAGCriterion(value: string): value is WCAGCriterion {
  return wcagCriterionCache.has(value);
}

export function isValidWCAGComplianceResult(value: unknown): value is WCAGComplianceResult {
  if (!value || typeof value !== "object") return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = value as any;

  return (
    typeof result.compliant === "boolean"
    && isValidWCAGLevel(result.level)
    && Array.isArray(result.violations)
    && Array.isArray(result.warnings)
    && Array.isArray(result.passed)
  );
}

export function isValidWCAGViolation(value: unknown): value is WCAGViolation {
  if (!value || typeof value !== "object") return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const violation = value as any;

  return (
    isValidWCAGCriterion(violation.criterion)
    && isValidWCAGLevel(violation.level)
    && typeof violation.message === "string"
    && ["critical", "serious", "moderate", "minor"].includes(violation.impact)
    && typeof violation.suggestion === "string"
  );
}

export function detectPrintableCharacter(key: string): PrintableCharacterInfo {
  if (printableCharacterCache.has(key)) {
    return printableCharacterCache.get(key)!;
  }

  let category: PrintableCharacterInfo["category"];
  let isPrintable = false;

  if (key.length === 1) {
    const code = key.charCodeAt(0);

    if ((code >= 65 && code <= 90) || (code >= 97 && code <= 122)) {
      //* A-Z, a-z
      category = "letter";
      isPrintable = true;
    } else if (code >= 48 && code <= 57) {
      //* 0-9
      category = "number";
      isPrintable = true;
    } else if (code === 32) {
      //* Space
      category = "whitespace";
      isPrintable = true;
    } else if (code >= 33 && code <= 126) {
      //* Printable symbols
      category = "symbol";
      isPrintable = true;
    } else if (code < 32 || code === 127) {
      //* Control characters
      category = "control";
      isPrintable = false;
    } else {
      //* Extended ASCII or Unicode
      category = "symbol";
      isPrintable = true;
    }
  } else {
    //* Multi-character keys (e.g., 'Enter', 'Escape', 'ArrowUp')
    const modifierKeys = ["Control", "Shift", "Alt", "Meta", "Ctrl", "Cmd"];

    if (modifierKeys.includes(key)) {
      category = "modifier";
    } else {
      category = "control";
    }
    isPrintable = false;
  }

  const result: PrintableCharacterInfo = {
    isPrintable,
    category,
    requiresWCAGCompliance: isPrintable, //* WCAG 2.1.4 applies to printable characters
  };

  printableCharacterCache.set(key, result);
  return result;
}

export function createDefaultWCAGComplianceResult(level: WCAGLevel = "AA"): WCAGComplianceResult {
  if (defaultWCAGComplianceResult === null || defaultWCAGComplianceResult.level !== level) {
    defaultWCAGComplianceResult = Object.freeze({
      compliant: true,
      level,
      violations: [],
      warnings: [],
      passed: [],
    });
  }

  //* Return a copy since this may be mutated
  return {
    compliant: defaultWCAGComplianceResult.compliant,
    level: defaultWCAGComplianceResult.level,
    violations: [...defaultWCAGComplianceResult.violations],
    warnings: [...defaultWCAGComplianceResult.warnings],
    passed: [...defaultWCAGComplianceResult.passed],
  };
}

const criterionSeverityCache = new Map<WCAGCriterion, "critical" | "serious" | "moderate" | "minor">();
export function getWCAGCriterionSeverity(criterion: WCAGCriterion): "critical" | "serious" | "moderate" | "minor" {
  if (criterionSeverityCache.has(criterion)) {
    return criterionSeverityCache.get(criterion)!;
  }

  let severity: "critical" | "serious" | "moderate" | "minor";

  switch (criterion) {
    case "2.1.1":
    case "2.1.2":
      severity = "critical";
      break;
    case "2.1.4":
    case "4.1.2":
      severity = "serious";
      break;
    case "2.4.3":
    case "2.4.7":
      severity = "moderate";
      break;
    default:
      severity = "minor";
  }

  criterionSeverityCache.set(criterion, severity);
  return severity;
}

const criterionDescriptionCache = new Map<WCAGCriterion, string>();
export function getWCAGCriterionDescription(criterion: WCAGCriterion): string {
  if (criterionDescriptionCache.has(criterion)) {
    return criterionDescriptionCache.get(criterion)!;
  }

  let description: string;

  switch (criterion) {
    case "2.1.1":
      description = "All functionality is available from a keyboard";
      break;
    case "2.1.2":
      description = "No keyboard trap - focus can be moved away from any component";
      break;
    case "2.1.4":
      description = "Character key shortcuts can be turned off, remapped, or are scoped to component focus";
      break;
    case "2.4.3":
      description = "Focus order is logical and intuitive";
      break;
    case "2.4.7":
      description = "Focus indicators are visible when components receive keyboard focus";
      break;
    case "4.1.2":
      description = "Name, role, value information available for assistive technologies";
      break;
    default:
      description = "WCAG criterion description not available";
  }

  criterionDescriptionCache.set(criterion, description);
  return description;
}

export function requiresWCAG214Compliance(key: string, modifiers: string[] = []): boolean {
  const charInfo = detectPrintableCharacter(key);

  /**
   * WCAG 2.1.4 applies to shortcut key command sequence combinations using:
   * @description Use printable characters, non-modifier command sequences, and/or Shift key ONLY (for uppercase letters)
   */

  if (!charInfo.isPrintable) return false;
  if (!modifiers || modifiers.length === 0) return true;
  if (modifiers.length === 1 && modifiers[0] === "shift" && charInfo.category === "letter") {
    return true;
  }
  return false;
}

export function createWCAGViolation(
  criterion: WCAGCriterion,
  message: string,
  suggestion: string,
  element?: HTMLElement,
  context?: Record<string, unknown>,
): WCAGViolation {
  const level: WCAGLevel = criterion.startsWith("2.1") || criterion === "2.4.3" || criterion === "2.4.7" ? "AA" : "A";
  const impact = getWCAGCriterionSeverity(criterion);

  return {
    criterion,
    level,
    message,
    impact,
    suggestion,
    element,
    context,
  };
}

export function clearWCAGUtilityCaches(): void {
  printableCharacterCache.clear();
  criterionSeverityCache.clear();
  criterionDescriptionCache.clear();
  defaultWCAGComplianceResult = null;
}

export function getWCAGUtilityCacheStats() {
  return {
    printableCharacterCache: printableCharacterCache.size,
    criterionSeverityCache: criterionSeverityCache.size,
    criterionDescriptionCache: criterionDescriptionCache.size,
    wcagLevelCacheSize: wcagLevelCache.size,
    wcagCriterionCacheSize: wcagCriterionCache.size,
    hasDefaultWCAGComplianceResult: defaultWCAGComplianceResult !== null,
  };
}
